import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import RadioGroup from '@/components/atom/RadioGroup';
import {
  DEFAULT_VALUES,
  formFields,
  formSchema,
} from '@/src/pages/pushAlarm/field';
import { NAV_CONST } from '@/src/constants/nav';
import { useCallback, useMemo, useRef, useState } from 'react';
import { usePrompt } from '@/hooks/usePrompt';
import { UpsertPushAlarmInfoRequestBody } from '@/src/apis/rest/pushAlarm.dtos';
import {
  getPushAlarmCategoryAPI,
  getPushAppPageConnectionCategoryAPI,
  postPushAlarmInfoAPI,
} from '@/src/apis/rest/pushAlarm';
import dayjs from 'dayjs';
import DatePicker from '@/components/atom/DatePicker';
import Select from '@/components/atom/Select';
import PushAlarmContentModal from '@/src/pages/pushAlarm/components/PushAlarmContentModal';
import TextArea from '@/components/atom/TextArea';
import {
  PushAlarmContentCategory,
  PushAlarmType,
} from '@/src/models/PushAlarmInfoDto';
import Typo from '@/components/atom/Typo';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import {
  CONNECT_CONTENT_OPTIONS,
  OPTIONS_CONST,
} from '@/src/constants/options';

const BACK_PATH = PATH.PUSH_ALARM_LIST;

function PushAlarmCreatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedTitle, setSelectedTitle] = useState('');
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [dataPage, setDataPage] = useState(1);
  const [dataPageSize, setDataPageSize] = useState(10);

  const form = useForm<UpsertPushAlarmInfoRequestBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
  });

  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);
  const watchedFields = useWatch({
    control: form.control,
  });

  const isReservation = watchedFields.pushAlarmType === 'reservation';

  const postPushAlarm = useMutation({
    mutationFn: postPushAlarmInfoAPI,
  });

  const reservationTime = useMemo(
    () => dayjs(watchedFields.reservationTime).toDate(),
    [watchedFields],
  );
  const selectedContentId = useMemo(
    () => watchedFields.pushAlarmContentId,
    [watchedFields],
  );

  const { data: pushCategoryTypeData } = useQuery({
    queryKey: ['push-alarm-category'],
    queryFn: () => {
      return getPushAlarmCategoryAPI();
    },
  });

  const { data: pushAppPageCategoryTypeData } = useQuery({
    queryKey: ['push-app-page-category'],
    queryFn: () => {
      return getPushAppPageConnectionCategoryAPI();
    },
  });

  const getContentTitleWithCategory = useCallback(
    (contentTitle: string, contentCategory: PushAlarmContentCategory) => {
      const categoryKr = contentCategory
        ? CONNECT_CONTENT_OPTIONS[contentCategory]
        : '-';
      return `[${categoryKr}] ${contentTitle || '-'}`;
    },
    [],
  );

  const handleOkClick = useCallback(
    (e: {
      id: number;
      title: string;
      category: PushAlarmContentCategory;
      dataPage: number;
      dataPageSize: number;
    }) => {
      const titleWithCategory = getContentTitleWithCategory(
        e.title,
        e.category,
      );
      setSelectedTitle(titleWithCategory);
      form.setValue(formFields.pushAlarmContentId.name, e.id);
      form.setValue('pushAlarmContentCategory', e.category);
      setDataPage(e.dataPage);
      setDataPageSize(e.dataPageSize);
    },
    [form, getContentTitleWithCategory],
  );

  const isFormValid = useMemo(() => {
    if (isReservation && !watchedFields.reservationTime) {
      return false;
    }
    return (
      watchedFields.pushCategoryType &&
      watchedFields.pushAlarmTitle &&
      watchedFields.pushAlarmContent &&
      (watchedFields.pushAlarmContentId ||
        watchedFields.pushAppPageConnectionCategory)
    );
  }, [isReservation, watchedFields]);

  // event
  const handleSubmit = useCallback(
    (values: UpsertPushAlarmInfoRequestBody) => {
      if (
        isReservation &&
        dayjs(values.reservationTime).toDate() < new Date()
      ) {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: '예약 시간은 현재 시간 이후로 설정해주세요.',
        });
        return;
      }

      postPushAlarm.mutate(values, {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH);
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      });
    },
    [isReservation, navigate, postPushAlarm, toast],
  );
  const handleResetContent = () => {
    form.setValue(formFields.pushAppPageConnectionCategory.name, undefined);
    form.setValue(formFields.pushAlarmContentId.name, undefined);
    form.setValue('pushAlarmContentCategory', undefined);
    setSelectedTitle('');
    setDataPage(1);
    setDataPageSize(10);
  };

  const renderConnectContent = () => {
    switch (form.watch('pushContentType')) {
      case 'appPageConnection':
        return (
          <FormField
            control={form.control}
            name={formFields.pushAppPageConnectionCategory.name}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <Select
                    rootProps={{
                      value: watchedFields.pushAppPageConnectionCategory,
                      onValueChange: e => {
                        field.onChange(e);
                        form.setValue(
                          formFields.pushAppPageConnectionCategory.name,
                          e,
                        );
                      },
                    }}
                    valueProps={{
                      placeholder: '연결할 앱 내 화면을 선택해주세요',
                    }}
                    option={[
                      {
                        optionList:
                          pushAppPageCategoryTypeData?.map(e => ({
                            label: e?.value,
                            value: e?.key,
                          })) ?? [],
                      },
                    ]}
                  />
                </FormContent>
              );
            }}
          />
        );
      case 'contentConnection':
        return (
          <FormField
            control={form.control}
            name={formFields.pushAlarmContentId.name}
            render={({ fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <>
                    <div className="flex gap-2">
                      <Input
                        value={selectedTitle}
                        disabled
                        containerClassName="flex-1"
                        placeholder="상세 연결 될 컨텐츠를 선택해주세요"
                      />
                      <Button
                        buttonSize="medium"
                        buttonColor="secondary"
                        className="h-[40px]"
                        onClick={() => setIsContentModalOpen(true)}
                        disabled={
                          isReservation && !form.watch('reservationTime')
                        }
                      >
                        연결 컨텐츠 선택
                      </Button>
                    </div>
                    <span className="mt-[4px] flex gap-[5px]">
                      <span className="flex h-[18px] items-center">
                        <LucideIcon name="Info" size={16} color="gray5" />
                      </span>
                      <Typo typoType="b14r" typoColor="gray5">
                        컨텐츠 상세 화면은 발송 일시 기준으로 노출되는 컨텐츠만
                        연결할 수 있어요
                      </Typo>
                    </span>
                  </>
                </FormContent>
              );
            }}
          />
        );
    }
  };

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => {
              navigate(-1);
            }}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={!isFormValid}
            type="button"
            onClick={openSaveDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={postPushAlarm.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.pushAlarmInfoManagement,
          NAV_CONST.pushAlarmInfoAdd,
        ]}
        pageTitle={NAV_CONST.pushAlarmInfoAdd}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px] flex flex-col gap-6"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="푸시 알림 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6">
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name={formFields.pushAlarmType.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="발송 유형" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            {
                              label: OPTIONS_CONST.pushInstant,
                              value: 'instant',
                            },
                            {
                              label: OPTIONS_CONST.pushReservation,
                              value: 'reservation',
                            },
                          ]}
                          rootProps={{
                            value: field.value,
                            onValueChange: e => {
                              field.onChange(e);
                              form.setValue(
                                formFields.pushAlarmType.name,
                                e as PushAlarmType,
                              );
                            },
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                {isReservation && (
                  <FormField
                    control={form.control}
                    name={formFields.reservationTime.name}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent isRequired label="발송 일시" error={error}>
                          <DatePicker
                            {...field}
                            isTimePicker
                            dateFormat="YYYY-MM-DD HH"
                            valueFormat="YYYY-MM-DD HH:mm:ss"
                            placeholder="YYYY-MM-DD HH"
                            hourStart="09"
                            hourEnd="21"
                            value={
                              field.value
                                ? dayjs(field.value).toDate()
                                : undefined
                            }
                            onChange={e => {
                              field.onChange(e);
                              form.setValue(
                                formFields.reservationTime.name,
                                e ? dayjs(e).format('YYYY-MM-DD HH:mm:ss') : '',
                              );
                            }}
                            disabledDate={date => dayjs().isAfter(date, 'D')}
                          />
                        </FormContent>
                      );
                    }}
                  />
                )}
                <FormField
                  control={form.control}
                  name={formFields.pushCategoryType.name}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent isRequired label="카테고리" error={error}>
                        <Select
                          rootProps={{
                            value: field?.value,
                            onValueChange: e =>
                              form.setValue(
                                formFields.pushCategoryType.name,
                                e,
                              ),
                          }}
                          valueProps={{
                            placeholder: '카테고리를 선택해주세요',
                          }}
                          option={[
                            {
                              optionList:
                                pushCategoryTypeData?.map(e => ({
                                  label: e?.value,
                                  value: e?.key,
                                })) ?? [],
                            },
                          ]}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name="pushContentType"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="컨텐츠 유형 선택"
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            {
                              label: '앱 내 화면',
                              value: 'appPageConnection',
                            },
                            {
                              label: '컨텐츠 상세 연결',
                              value: 'contentConnection',
                            },
                          ]}
                          rootProps={{
                            value: field.value,
                            onValueChange: e => {
                              field.onChange(e);
                              handleResetContent();
                            },
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                {renderConnectContent()}
              </div>
            </div>
            <div className="mt-[20px] flex flex-col gap-y-6">
              <FormField
                control={form.control}
                name={formFields.pushAlarmTitle.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent label="제목" isRequired error={error}>
                      <Input
                        containerClassName="flex-1"
                        placeholder="제목을 입력해주세요"
                        onClear={() => field.onChange('')}
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={formFields.pushAlarmContent.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent label="내용" isRequired error={error}>
                      <TextArea
                        placeholder="내용을 입력해주세요"
                        className="h-[230px]"
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>
      {isContentModalOpen && (
        <PushAlarmContentModal
          selectedCategory={watchedFields.pushAlarmContentCategory}
          selectedId={selectedContentId}
          open={isContentModalOpen}
          onOpenChange={setIsContentModalOpen}
          onOkClick={handleOkClick}
          reservationTime={reservationTime}
          dataPage={dataPage}
          dataPageSize={dataPageSize}
        />
      )}
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={
            form.watch('pushAlarmType') === 'instant'
              ? '즉시 발송 푸시 알림'
              : COMMON_ALERT_DIALOG_CONST.save
          }
          isLoading={postPushAlarm.isPending}
        >
          {form.watch('pushAlarmType') === 'instant' && (
            <div className="whitespace-pre-wrap">
              등록 시 바로 사용자들에게 푸시가 발송되며 취소할 수
              없습니다.\n바로 발송 하시겠습니까?
            </div>
          )}
        </SaveDialog>
      )}
    </>
  );
}

export default PushAlarmCreatePage;
